import React from 'react';
import { createStore as reduxCreateStore } from 'redux';
import { Provider, createStoreHook, createDispatchHook, createSelectorHook } from 'react-redux';

const identity = state => state;

class StoreBase {
  constructor() {
    const proto = this.constructor;
    this.store = reduxCreateStore(proto.reducer, proto.initialState);
    this.dispatch = this.store.dispatch;
    if (proto.actions) {
      for (const type in proto.actions) {
        this.dispatch[type] = (...args) => this.dispatch({type, args});
      }
    }
  }

  static Provider = ({ store, children }) => (
    <Provider context={store.constructor.context} store={store.store}>
      {children}
    </Provider>
  );

  static useSelector(func, depends) {
    func = func || identity;
    // eslint-disable-next-line react-hooks/rules-of-hooks,react-hooks/exhaustive-deps
    if (depends) func = React.useCallback(func, depends);
    return this.selector(func);
  }

  static useState() {
    return this.selector(identity);
  }

  getState() {
    return this.store.getState();
  }

  subscribe(selector, callback) {
    let prevState = selector(this.store.getState());
    this.store.subscribe(() => {
      const state = selector(this.store.getState());
      if (state !== prevState) {
        prevState = state;
        callback(state);
      }
    });
  }
}

export default function createStore(reducers, initialState) {
  class Store extends StoreBase {};
  Store.context = React.createContext(null);
  if (typeof reducers === "function") {
    Store.reducer = reducers;
  } else {
    Store.actions = reducers;
    Store.reducer = (state, action) => (reducers[action.type] ? reducers[action.type](state, ...action.args) : state);
  }
  Store.initialState = initialState;
  Store.useStore = createStoreHook(Store.context);
  Store.useDispatch = createDispatchHook(Store.context);
  Store.selector = createSelectorHook(Store.context);
  return Store;
}
